import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, Quote, User } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    rating: 5,
    comment: "",
    car_type: ""
  });

  // Static reviews for display
  const staticReviews = [
    {
      id: "1",
      name: "Demo Ügyfél 1",
      rating: 5,
      comment: "Lenyűgöző munka! Az autóm olyan, mintha most gurult volna ki a szalonból. A kerámia bevonat tökéletes lett, már több hónap eltelt és még mindig ragyog. (Demo vélemény)",
      car_type: "BMW M5",
      created_at: "2024-01-15"
    },
    {
      id: "2",
      name: "Demo Ügyfél 2",
      rating: 5,
      comment: "Profi csapat, prémium minőség. A belső tisztítás után olyan az illat, mint egy új autóban. Csak ajánlani tudom mindenkinek! (Demo vélemény)",
      car_type: "Mercedes E-Class",
      created_at: "2024-02-20"
    },
    {
      id: "3",
      name: "Demo Ügyfél 3",
      rating: 5,
      comment: "Évek óta kerestem egy megbízható autókozmetikát. Végre megtaláltam. A polírozás eredménye páratlan, minden karcolás eltűnt. (Demo vélemény)",
      car_type: "Porsche 911",
      created_at: "2024-03-10"
    },
    {
      id: "4",
      name: "Demo Ügyfél 4",
      rating: 5,
      comment: "Kiváló ár-érték arány! A teljes detailing csomag minden pénzt megért. Az autóm olyan lett, mint amikor átvettem a kereskedéstől. (Demo vélemény)",
      car_type: "Audi RS6",
      created_at: "2024-03-25"
    },
    {
      id: "5",
      name: "Demo Ügyfél 5",
      rating: 5,
      comment: "Megbízható, pontos és precíz munka. A bőrülések tisztítása után teljesen megújult a belső tér. Legközelebb is ide hozom az autómat! (Demo vélemény)",
      car_type: "Range Rover",
      created_at: "2024-04-05"
    },
    {
      id: "6",
      name: "Demo Ügyfél 6",
      rating: 5,
      comment: "Az ózonos fertőtlenítés csodát tett! Elképesztő, hogy a kellemetlen szagok teljesen eltűntek. Köszönöm a gyors és hatékony munkát! (Demo vélemény)",
      car_type: "Volkswagen Passat",
      created_at: "2024-04-18"
    },
  ];

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        setReviews([...staticReviews, ...response.data]);
      } catch (error) {
        console.error("Error fetching reviews:", error);
        setReviews(staticReviews);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRatingChange = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.comment) {
      toast.error("Kérjük, töltse ki a kötelező mezőket!");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, formData);
      toast.success("Köszönjük véleményét! A jóváhagyás után megjelenik az oldalon.");
      setFormData({ name: "", rating: 5, comment: "", car_type: "" });
      setShowForm(false);
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Hiba történt a vélemény elküldése közben.");
    } finally {
      setSubmitting(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : "5.0";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="reviews-page" className="bg-[#050505]">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-6">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4 block">
              Vélemények
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl text-[#E5E5E5] mb-6">
              ÜGYFELEINK
              <span className="block text-[#D4AF37]">VÉLEMÉNYE</span>
            </h1>
            <p className="text-[#A3A3A3] text-lg leading-relaxed">
              Olvassa el elégedett ügyfeleink visszajelzéseit, és győződjön meg munkánk minőségéről.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-[#0A0A0A] border-t border-[#2A2A2A]">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="font-heading text-5xl text-[#D4AF37]">{averageRating}</span>
                <Star size={32} className="text-[#D4AF37] fill-[#D4AF37]" />
              </div>
              <p className="text-[#A3A3A3]">Átlagos értékelés</p>
            </div>
            <div className="h-16 w-px bg-[#2A2A2A] hidden md:block" />
            <div className="text-center">
              <span className="font-heading text-5xl text-[#E5E5E5]">{reviews.length}+</span>
              <p className="text-[#A3A3A3]">Elégedett ügyfél</p>
            </div>
            <div className="h-16 w-px bg-[#2A2A2A] hidden md:block" />
            <div className="text-center">
              <span className="font-heading text-5xl text-[#E5E5E5]">10+</span>
              <p className="text-[#A3A3A3]">Év tapasztalat</p>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-[#121212] border border-[#2A2A2A] p-6 hover:border-[#D4AF37] transition-colors"
                data-testid={`review-${review.id}`}
              >
                <Quote size={32} className="text-[#D4AF37] mb-4 opacity-50" />
                
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className={i < review.rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-[#2A2A2A]"}
                    />
                  ))}
                </div>
                
                <p className="text-[#A3A3A3] mb-6 leading-relaxed font-accent italic">
                  "{review.comment}"
                </p>
                
                <div className="flex items-center gap-3 pt-4 border-t border-[#2A2A2A]">
                  <div className="w-10 h-10 bg-[#1E1E1E] rounded-full flex items-center justify-center">
                    <User size={20} className="text-[#666]" />
                  </div>
                  <div>
                    <p className="font-heading text-[#E5E5E5]">{review.name}</p>
                    {review.car_type && (
                      <p className="text-[#D4AF37] text-sm">{review.car_type}</p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Write Review */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="container mx-auto px-6">
          <motion.div {...fadeInUp} className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-3xl text-[#E5E5E5] mb-4">ÍRJON VÉLEMÉNYT</h2>
            <p className="text-[#A3A3A3] mb-8">
              Volt már nálunk? Ossza meg tapasztalatait másokkal!
            </p>

            {!showForm ? (
              <Button
                onClick={() => setShowForm(true)}
                data-testid="show-review-form"
                className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest hover:bg-[#B5952F] px-8 py-4 rounded-sm"
              >
                Vélemény Írása
              </Button>
            ) : (
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                onSubmit={handleSubmit}
                className="bg-[#121212] border border-[#2A2A2A] p-8 text-left"
                data-testid="review-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="review-name" className="text-[#A3A3A3] mb-2 block">Név *</Label>
                    <Input
                      id="review-name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Teljes név"
                      data-testid="review-input-name"
                      className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#D4AF37] text-[#E5E5E5] placeholder:text-[#666]"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="review-car" className="text-[#A3A3A3] mb-2 block">Autó típusa</Label>
                    <Input
                      id="review-car"
                      name="car_type"
                      value={formData.car_type}
                      onChange={handleInputChange}
                      placeholder="pl. BMW M5"
                      data-testid="review-input-car"
                      className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#D4AF37] text-[#E5E5E5] placeholder:text-[#666]"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <Label className="text-[#A3A3A3] mb-2 block">Értékelés *</Label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleRatingChange(star)}
                        className="p-1 transition-transform hover:scale-110"
                        data-testid={`rating-star-${star}`}
                      >
                        <Star
                          size={32}
                          className={star <= formData.rating 
                            ? "text-[#D4AF37] fill-[#D4AF37]" 
                            : "text-[#2A2A2A]"
                          }
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <Label htmlFor="review-comment" className="text-[#A3A3A3] mb-2 block">Vélemény *</Label>
                  <Textarea
                    id="review-comment"
                    name="comment"
                    value={formData.comment}
                    onChange={handleInputChange}
                    placeholder="Ossza meg tapasztalatait..."
                    rows={4}
                    data-testid="review-input-comment"
                    className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#D4AF37] text-[#E5E5E5] placeholder:text-[#666]"
                    required
                  />
                </div>

                <div className="flex gap-4">
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="submit-review"
                    className="flex-1 bg-[#D4AF37] text-black font-bold uppercase tracking-widest hover:bg-[#B5952F] py-4 rounded-sm"
                  >
                    {submitting ? "Küldés..." : "Vélemény Elküldése"}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowForm(false)}
                    variant="outline"
                    className="bg-transparent border border-[#2A2A2A] text-[#A3A3A3] hover:border-[#D4AF37] hover:text-[#D4AF37] px-6 py-4 rounded-sm"
                  >
                    Mégse
                  </Button>
                </div>
              </motion.form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ReviewsPage;
